// How-to guide content for the "Play a Game" reference. Rules from the iOS
// GolfGames rule cards; per game we show setup WITHOUT bets (points/fun) and
// WITH bets ($ options + how money settles). Plus one shared handicap explainer.
// Shape: { players, how:[bullets], noBet, withBet, example:{ setup, players, walk } }.
export const GAME_GUIDES = {
  wolf: {
    players: '3–6 players',
    how: [
      'One player is the "Wolf" each hole; the Wolf rotates by the player order you set.',
      'The Wolf tees off first, then watches each opponent drive. After any drive, the Wolf may take that player as a partner — but must decide before the next player hits.',
      'Pick nobody and you go Lone Wolf: 1 against the rest for bigger stakes. Blind Wolf and Howl each double the stake.',
      'Lowest ball on each side wins the hole.',
    ],
    noBet: 'Just for fun: track who wins each hole (Wolf’s side vs the field) — bragging rights for reading the drives best. No money needed.',
    withBet: 'For money: set $/hole and per-winner or split-pot. Each hole the winning side collects the stake; a Lone Wolf plays every opponent one-on-one (no multiplier), and Blind and Howl multiply that hole’s stake.',
    example: {
      setup: 'Variant Standard · $/hole $2 · Partner payout per-winner · Blind/Howl off · Stroke mode lowest.',
      players: 'Al, Bo, Cy, Di — the order is the Wolf rotation.',
      walk: 'Hole 1 Al is the Wolf; Bo stripes his drive so Al partners Bo. Al+Bo best 4 beats Cy+Di best 5 → Al and Bo each win $2. Hole 2 the Wolf passes to Bo.',
    },
  },
  nines: {
    players: 'Exactly 3 players',
    how: [
      '9 points are up for grabs every hole among the three players.',
      'Lowest score = 5, middle = 3, highest = 1. Two tied for low = 4/4/1; all three tied = 3/3/3.',
      'Tally points across all 18 holes; highest total wins.',
    ],
    noBet: 'Just for fun: play for the points — highest total after 18 wins.',
    withBet: 'For money: set $/point. At the end, each pair settles the difference in their point totals × $ (zero-sum).',
    example: {
      setup: 'No bet: just watch the point totals.  ·  Bet: $/point $2.',
      players: 'Al, Bo, Cy.',
      walk: 'Al 59 pts, Bo 54, Cy 49. For fun, Al wins. At $2/pt, Al +$30, Bo even, Cy −$30.',
    },
  },
  chicago: {
    players: '2–8 players',
    how: [
      'Points vs par each hole: double eagle 8, eagle 5, birdie 2, par 1, bogey-or-worse 0.',
      'Each player has a quota tied to handicap (here quota = 39 − handicap).',
      'Beat your own quota by the most to win the round.',
    ],
    noBet: 'Just for fun: whoever beats their quota by the most wins — no money.',
    withBet: 'For money: set $/point. Settle $ per point of vs-quota difference between each pair (zero-sum).',
    example: {
      setup: 'No bet: chase your quota.  ·  Bet: $/point $1 · Handicaps on.',
      players: 'Al (hcp 5), Bo (hcp 10), Cy (hcp 18).',
      walk: 'Bo finishes +6 over quota, Al −6, Cy 0. For fun, Bo wins. At $1/pt Bo collects on the vs-quota spread.',
    },
  },
  rabbit: {
    players: '3–8 players',
    how: [
      'Catch the rabbit by winning a hole outright (lowest score, no ties).',
      'Hold it until someone else wins a hole outright; tied holes = the rabbit runs (no change).',
      'Whoever holds the rabbit after hole 9 takes the front; after 18, the back.',
    ],
    noBet: 'Just for fun: chase the rabbit — hold it at the turn and the finish for bragging rights.',
    withBet: 'For money: set a Front pot and Back pot ($). The group funds each pot equally; the holder takes it.',
    example: {
      setup: 'No bet: just hold the rabbit.  ·  Bet: Front $10 · Back $10.',
      players: 'Al, Bo, Cy, Di.',
      walk: 'Bo wins hole 7 outright and still holds at the turn → takes the $10 front pot (others chip in). New race starts for the back.',
    },
  },
  matchplay: {
    players: 'Exactly 2 players',
    how: [
      'Head-to-head, hole by hole. Win a hole by the lower score; equal score = halved.',
      'You track holes won, not total strokes.',
    ],
    noBet: 'Just for fun: play for the match result (e.g. "3&2" — 3 up with 2 to play).',
    withBet: 'For money: set $/hole. Win a hole = win the stake; a halved hole moves nothing.',
    example: {
      setup: 'No bet: play the match.  ·  Bet: $/hole $5.',
      players: 'Al vs Bo.',
      walk: 'Al wins holes 1–4 and loses 18 → 3 holes up. For fun that’s a 3-up win; at $5/hole, +$15 for Al.',
    },
  },
  dogfight: {
    players: '2–8 players',
    how: [
      'Quota = 36 − playing handicap. Points vs par: eagle 4, birdie 3, par 2, bogey 1, double-or-worse 0.',
      'Beat your quota by the most to win; if nobody beats it, closest to quota wins.',
    ],
    noBet: 'Just for fun: most points over quota wins the round.',
    withBet: 'For money: set $/point. Settle pairwise on vs-quota (each pair trades the point difference).',
    example: {
      setup: 'No bet: chase your quota.  ·  Bet: $/point $1 · Quota 36 · Handicaps on.',
      players: 'Al, Bo, Cy.',
      walk: 'Al +18 vs quota, Bo 0, Cy −18. For fun Al wins; at $1/pt Al collects $54 across the field.',
    },
  },
  'stroke-play': {
    players: '1–8 players',
    how: [
      'The classic format — every stroke counts. Lowest 18-hole total wins.',
      'Turn on Handicaps and your score becomes gross minus handicap.',
    ],
    noBet: 'Just for fun: lowest total (gross or net) wins — a simple leaderboard.',
    withBet: 'For money: Stroke Play has no built-in pot here (same as your app) — add a side bet (Skins or Scats) if you want stakes.',
    example: {
      setup: 'No bet: just the leaderboard.  ·  Bet: add Skins/Scats.',
      players: 'Al, Bo, Cy.',
      walk: 'Bo 72, Al 76, Cy 81 → Bo wins the round.',
    },
  },
  vegas: {
    players: 'Exactly 4 (two teams of 2)',
    how: [
      'Each hole, combine your team’s two scores into a two-digit number, lower first (a 4 and 5 = "45").',
      'The lower team number wins the difference in points.',
      'A birdie flips (reverses) the opponents’ number, magnifying the swing.',
    ],
    noBet: 'Just for fun: play for the team point total — most points wins.',
    withBet: 'For money: set $/point. The team point-difference × $ is split between partners (zero-sum).',
    example: {
      setup: 'No bet: track team points.  ·  Bet: $/point $1 · Team A = Al & Bo, B = Cy & Di.',
      players: 'Al, Bo, Cy, Di.',
      walk: 'Team A nets +41 points. For fun A wins; at $1/pt Al & Bo each +$41, Cy & Di each −$41.',
    },
  },
  scotch: {
    players: 'Exactly 4 (two teams of 2)',
    how: [
      'Two teams of two, match play by team best score each hole.',
      'Lower team score wins the hole; track holes up/down over the round.',
    ],
    noBet: 'Just for fun: play the team match — who’s up at the end.',
    withBet: 'For money: set $/hole. Team holes-up × $/hole, split between partners.',
    example: {
      setup: 'No bet: play the match.  ·  Bet: $/hole $2 · Team A = Al & Bo, B = Cy & Di.',
      players: 'Al, Bo, Cy, Di.',
      walk: 'Team A finishes 3 up. For fun A wins; at $2/hole that’s +$6 team → Al & Bo each +$3.',
    },
  },
  'four-ball': {
    players: 'Exactly 4 (two teams of 2)',
    how: [
      'Everyone plays their own ball. Each team takes its best ball (lowest) on every hole.',
      'The two best balls play match play; the team winning more holes wins.',
    ],
    noBet: 'Just for fun: play the best-ball match — who wins more holes.',
    withBet: 'For money: set $/hole. Team holes-up × $/hole, split between partners.',
    example: {
      setup: 'No bet: play the match.  ·  Bet: $/hole $5 · Team A = Al & Bo, B = Cy & Di.',
      players: 'Al, Bo, Cy, Di.',
      walk: 'Team A wins holes 1–3 on best ball and ties the rest → 3 up. At $5/hole, +$15 team → each +$7.50.',
    },
  },
  dots: {
    players: '2–8 players',
    how: [
      'Earn a dot for each birdie-or-better (this build auto-scores birdie dots from the card, matching your iOS scorecard).',
      'In the full app, junk dots (greenie, sandy, barkie…) are also tapped — those aren’t collected in the shared scorecard.',
    ],
    noBet: 'Just for fun: count dots — most dots wins.',
    withBet: 'For money: set $/dot. Settle pairwise on dot count × $ (zero-sum).',
    example: {
      setup: 'No bet: count the dots.  ·  Bet: $/dot $2.',
      players: 'Al, Bo, Cy.',
      walk: 'Al 3 birdies (3 dots), Bo 1, Cy 0. For fun Al wins; at $2/dot Al +$10, Bo −$2, Cy −$8.',
    },
  },
  bbb: {
    players: '2–8 players',
    how: [
      'Three points every hole — tap the player who earned each on the scorecard:',
      'Bingo = first on the green · Bango = closest once all are on · Bongo = first in the hole.',
      'Most total points wins.',
    ],
    noBet: 'Just for fun: play for the points — most Bingo/Bango/Bongo wins.',
    withBet: 'For money: set $/point. Settle pairwise on points × $ (zero-sum).',
    example: {
      setup: 'No bet: tap the awards.  ·  Bet: $/point $1.',
      players: 'Al, Bo, Cy.',
      walk: 'Bo 4 pts, Al 3, Cy 2. For fun Bo wins; at $1/pt Bo +$6, Al even, Cy −$6.',
    },
  },
  snake: {
    players: '2–8 players',
    how: [
      'A side game. Tap whoever three-putts a hole — they hold the snake until someone else three-putts.',
      'Whoever holds it at the end is the loser.',
      '(Your iOS shared scorecard doesn’t tap three-putts, so it pays $0 there — the tap here makes it actually play.)',
    ],
    noBet: 'Just for fun: the last three-putter eats the shame — no money.',
    withBet: 'For money: set the Snake $. The final holder pays that to every other player (zero-sum).',
    example: {
      setup: 'No bet: just the shame.  ·  Bet: Snake $5.',
      players: 'Al, Bo, Cy.',
      walk: 'Cy three-putts the 17th and no one else does → Cy pays $5 each → Cy −$10, Al +$5, Bo +$5.',
    },
  },
  nassau: {
    players: '2–8 players',
    how: [
      'Three matches in one round: front 9, back 9, and overall 18 (lowest wins each).',
      'A press starts a fresh match when a side falls behind; presses auto-open at 2 down.',
    ],
    noBet: 'Just for fun: win the front, the back, the overall — three bragging-rights matches.',
    withBet: 'For money: set $/bet. Each match (and each press) is worth the stake, settled pairwise.',
    example: {
      setup: 'No bet: play the three matches.  ·  Bet: $/bet $5.',
      players: 'Al vs Bo (heads-up).',
      walk: 'Al wins the front, Bo the back, presses swing the total → Bo +$35, Al −$35 at $5/bet.',
    },
  },
  sixes: {
    players: 'Exactly 4 players',
    how: [
      'Partners rotate every 6 holes — three mini-matches: 1&2 vs 3&4, then 1&3 vs 2&4, then 1&4 vs 2&3.',
      'Best cumulative team score across the three segments wins; everyone plays with everyone.',
    ],
    noBet: 'Just for fun: most segment wins tops the board.',
    withBet: 'For money: Sixes has no built-in pot here (same as your app) — add a side bet if you want stakes.',
    example: {
      setup: 'No bet: track segment wins.  ·  Bet: add a side bet.',
      players: 'Al, Bo, Cy, Di (order sets the pairings).',
      walk: 'Across the three 6-hole segments Al’s teams win the most → Al tops the standings.',
    },
  },
  shamble: {
    players: 'Exactly 4 (two teams of 2)',
    how: [
      'Everyone tees off, the team picks the best drive, then each player plays their own ball in from there.',
      'The team takes the best individual score on the hole.',
    ],
    noBet: 'Just for fun: low team total wins the board.',
    withBet: 'For money: Shamble has no built-in pot here (same as your app) — add a side bet if you want stakes.',
    example: {
      setup: 'No bet: track team totals.  ·  Bet: add a side bet.',
      players: 'Al, Bo, Cy, Di · Team A = Al & Bo, B = Cy & Di.',
      walk: 'Team A’s best-of-two totals 68, Team B 71 → Team A leads.',
    },
  },
  shuffle: {
    players: '3–6 players',
    how: [
      'Partners rotate every hole so you play with and against everyone.',
      'Each hole the team with the lower best-ball wins; 3-player mode rotates one sit-out per hole.',
    ],
    noBet: 'Just for fun: track which team wins each hole as partners rotate.',
    withBet: 'For money: set $/hole. The winning team collects $/hole from the losing team every hole.',
    example: {
      setup: 'No bet: track hole winners.  ·  Bet: $/hole $2.',
      players: 'Al, Bo, Cy, Di (order seeds the rotation).',
      walk: 'Al plays his ball best all day and is on the winning team most holes → Al finishes up at $2/hole.',
    },
  },
  'team-cup': {
    players: 'Exactly 4 (two teams of 2)',
    how: [
      'Two teams; each team’s best ball plays match play by hole. Win a hole = 1 point, halve = ½ each.',
      'Optional Closest-to-Pin on par 3s: tap the closest player.',
    ],
    noBet: 'Just for fun: play for the cup — team match points decide it (no money on the points).',
    withBet: 'For money: the Closest-to-Pin pot is the only cash — set CTP pot $/par 3; the tapped closest player takes each pot (carries if none picked).',
    example: {
      setup: 'No bet: play for the cup.  ·  Bet: CTP pot $5/par 3 · Team A = Al & Bo, B = Cy & Di.',
      players: 'Al, Bo, Cy, Di.',
      walk: 'Team A wins the match 12–6 on best-ball points. On each par 3 the tapped closest player collects the $5 pot.',
    },
  },
}

// ── ONE shared handicap explainer — same for every game ──────────────────────
export const HANDICAP_GUIDE = {
  purpose: 'Handicaps make any game competitive no matter your handicap — high or low. A 3-handicap and a 15-handicap can play a real match. Turn on “Handicaps (net)” in setup for any game and everyone competes on a level field.',
  steps: [
    ['1. Enter your handicap', 'Type your Handicap Index (for example 14.2) in the player’s handicap box. If you have a GHIN number you can type it and the app looks your index up for you — but GHIN is optional; typing your index in yourself works exactly the same. No GHIN needed.'],
    ['2. Pick your tee', 'Each player picks their own tee (Black, Blue, White…). That tee’s rating and slope are used in the math, so a player from the back tees and a player from the forward tees are still compared fairly.'],
    ['3. Allowance (usually leave it at 100%)', 'Most games use 100%. Some team formats knock it down (95% four-ball, 85% best-two, 80% scramble). Set it once for everyone, or per player — leave it at 100% if you’re not sure.'],
    ['4. The app does the math', 'It figures your Course Handicap (your index adjusted for the tee’s slope and rating), then gives out strokes on the hardest holes first. You still enter your real (gross) score — the app quietly subtracts your strokes so every bet settles net and fair.'],
  ],
  example: {
    title: 'Example — a 3 vs a 15',
    lines: [
      'The 3-handicap gets one stroke on only the 3 hardest holes. The 15-handicap gets a stroke on 15 of the 18 holes (all but the 3 easiest).',
      'On a hard hole, both get a stroke — dead even. On a middling hole, only the 15 gets one — that’s the gap between them handed to the higher handicap exactly where the hole is hard enough to earn it.',
      'So if the 15 makes a 5 on a hole where they get a stroke, it counts as a net 4 — matching the 3-handicap’s real 4. The match stays fair and nobody has to do the math in their head.',
    ],
  },
}
